import {
  defineQuery,
  type Query,
  type QueryDefinition,
  type ReadonlyJSONValue,
  type Schema,
} from '@rocicorp/zero';
import type { OrgSessionData } from '@strength/server-auth';
import z from 'zod';
import { createOrgScopedQuery, type OrgScopedQuery } from './utils';

/**
 * Turns a shared tRPC input schema into a Zero mutator arg schema by adding the
 * team scoping the Zero payload carries explicitly (tRPC injects `teamId` from
 * its `teamProcedure`; the Zero arg payload includes it). Every base field keeps
 * its inference, so `z.infer` of the result includes the input plus `teamId`.
 */
export function zeroSchema<T extends z.ZodRawShape>(input: z.ZodObject<T>) {
  return input.extend({ teamId: z.uuid() });
}

/** Validates requested team IDs against the user's accessible teams. */
export function validateZeroTeamAccess(
  accessibleTeamIds: string[] | null,
  requestedTeamIds: string[],
): [string, ...string[]] {
  if (requestedTeamIds.length === 0) {
    throw new Error('teamIds is required for team-scoped queries');
  }

  if (accessibleTeamIds === null) {
    return requestedTeamIds as [string, ...string[]];
  }

  for (const teamId of requestedTeamIds) {
    if (!accessibleTeamIds.includes(teamId)) {
      throw new Error(
        `Insufficient permissions: team ${teamId} is not accessible`,
      );
    }
  }

  return requestedTeamIds as [string, ...string[]];
}

/**
 * Wraps defineQuery (mirrors tRPC's orgProcedure), injecting the org-scoped
 * `orgQuery` builder. Query authors start every table query from `orgQuery`,
 * which always applies the orgId filter, so an org-unscoped query cannot be
 * built by accident. `ctx` is still provided for other session values (e.g.
 * profileId).
 *
 * INVARIANT (queryTableName in normalize-zero-row.ts relies on it): every
 * definition must start from ONE unconditional `orgQuery('<Table>')` call.
 * The read funnels derive a named query's root table by re-running the
 * definition against a permissive context; a definition that picks its root
 * conditionally (on ctx or args) would derive a wrong table name there.
 *
 * Two forms, matching defineQuery: with a Zod validator (args are injected) or
 * without (no args).
 */
export function defineOrgQuery<
  TTable extends keyof Schema['tables'] & string,
  TReturn,
>(
  fn: (opts: {
    ctx: OrgSessionData;
    orgQuery: OrgScopedQuery;
  }) => Query<TTable, Schema, TReturn>,
): QueryDefinition<TTable, undefined, undefined, TReturn, OrgSessionData>;
export function defineOrgQuery<
  TArgs extends ReadonlyJSONValue,
  TTable extends keyof Schema['tables'] & string,
  TReturn,
>(
  validator: z.ZodType<TArgs, TArgs>,
  fn: (opts: {
    args: TArgs;
    ctx: OrgSessionData;
    orgQuery: OrgScopedQuery;
  }) => Query<TTable, Schema, TReturn>,
): QueryDefinition<TTable, TArgs, TArgs, TReturn, OrgSessionData>;
export function defineOrgQuery<
  TArgs extends ReadonlyJSONValue,
  TTable extends keyof Schema['tables'] & string,
  TReturn,
>(
  validatorOrFn:
    | z.ZodType<TArgs, TArgs>
    | ((opts: {
        ctx: OrgSessionData;
        orgQuery: OrgScopedQuery;
      }) => Query<TTable, Schema, TReturn>),
  maybeFn?: (opts: {
    args: TArgs;
    ctx: OrgSessionData;
    orgQuery: OrgScopedQuery;
  }) => Query<TTable, Schema, TReturn>,
) {
  if (maybeFn) {
    const validator = validatorOrFn as z.ZodType<TArgs, TArgs>;
    return defineQuery<TArgs, TArgs, OrgSessionData, Schema, TTable, TReturn>(
      validator,
      ({ args, ctx }) =>
        maybeFn({ args, ctx, orgQuery: createOrgScopedQuery(ctx) }),
    );
  }
  const fn = validatorOrFn as (opts: {
    ctx: OrgSessionData;
    orgQuery: OrgScopedQuery;
  }) => Query<TTable, Schema, TReturn>;
  return defineQuery<undefined, OrgSessionData, Schema, TTable, TReturn>(
    ({ ctx }) => fn({ ctx, orgQuery: createOrgScopedQuery(ctx) }),
  );
}

/**
 * Wraps defineQuery with multi-team access validation (mirrors tRPC's multiTeamProcedure).
 * The validator's output must include a `teamIds` field; the validated IDs are
 * injected into the query fn as `teamIds`, alongside the org-scoped `orgQuery`
 * builder. Types thread through to defineQuery so the resulting query keeps its
 * arg/return inference (no `any`).
 */
export function defineMultiTeamQuery<
  TArgs extends { teamIds: string[] },
  TTable extends keyof Schema['tables'] & string,
  TReturn,
>(
  validator: z.ZodType<TArgs, TArgs>,
  fn: (opts: {
    args: TArgs;
    ctx: OrgSessionData;
    teamIds: [string, ...string[]];
    orgQuery: OrgScopedQuery;
  }) => Query<TTable, Schema, TReturn>,
) {
  return defineQuery<TArgs, TArgs, OrgSessionData, Schema, TTable, TReturn>(
    validator,
    ({ args, ctx }) => {
      const teamIds = validateZeroTeamAccess(ctx.teamIds, args.teamIds ?? []);
      return fn({ args, ctx, teamIds, orgQuery: createOrgScopedQuery(ctx) });
    },
  );
}

/**
 * Wraps defineQuery with single-team access validation (mirrors tRPC's teamProcedure).
 * The validator's output must include a `teamIds` field; the first validated ID
 * is exposed to the query fn as `teamId`, alongside the org-scoped `orgQuery`
 * builder.
 */
export function defineTeamQuery<
  TArgs extends { teamIds: string[] },
  TTable extends keyof Schema['tables'] & string,
  TReturn,
>(
  validator: z.ZodType<TArgs, TArgs>,
  fn: (opts: {
    args: TArgs;
    ctx: OrgSessionData;
    teamId: string;
    orgQuery: OrgScopedQuery;
  }) => Query<TTable, Schema, TReturn>,
) {
  return defineQuery<TArgs, TArgs, OrgSessionData, Schema, TTable, TReturn>(
    validator,
    ({ args, ctx }) => {
      const [teamId] = validateZeroTeamAccess(ctx.teamIds, args.teamIds ?? []);
      return fn({ args, ctx, teamId, orgQuery: createOrgScopedQuery(ctx) });
    },
  );
}
